import { ChevronDoubleDownIcon, ClockIcon } from "@heroicons/react/24/outline";
import SimpleProgress from "../Progress/SimpleProgress";
import { classNames } from "../../style/common";
import { KoFiIcon } from "../Icons/KoFiIcon";
import { Tooltip, TooltipContent, TooltipTrigger } from "../Tooltip/Tooltip";
import { SiteCost } from "../../models/SiteCost";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useState } from "react";
import { FIREBASE_STORAGE_URL } from "../../app-settings";

export type SiteCostProps = {
    classNames?: {
        container?: string
    }
}

export function SiteCostTracker(props: SiteCostProps) {
    const siteCost = useSelector<RootState, SiteCost>(x => x.app.siteCost);

    const [isProgressHover, setIsProgressHover] = useState(false);

    return (
        <a
            href="https://ko-fi.com/materiahunter"
            target="_blank"
            className={classNames(props.classNames?.container, "cursor-pointer select-none text-contrast-primary")}
            onMouseEnter={() => setIsProgressHover(true)} onMouseLeave={() => setIsProgressHover(false)}
        >
            <div className="flex justify-between items-center">
                <Tooltip>
                    <TooltipTrigger>
                        <div className="font-semibold flex items-center gap-1">
                            <KoFiIcon classNames={{ svg: "w-5 h-5 ml-[1px] mt-[1px]", path: "fill-contrast-primary" }} />
                            <span>Site Level {siteCost.level}</span>
                        </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-full w-96 px-4" style={{ zIndex: 100 }}>
                        <div className="rounded-md bg-gray-900 border border-color-border shadow p-4 text-sm text-white">
                            <div>Times the site has leveled up.</div>
                            <div className="text-contrast-tertiary text-xs mb-2">Months site costs were 100% covered by Materia Hunter's wonderful supporters.</div>

                            <div className="text-contrast-tertiary text-xs">Level up on the first of each month if xp reaches 100%.</div>
                        </div>
                    </TooltipContent>
                </Tooltip>

                {
                    siteCost.debuff > 0 &&
                    <Tooltip>
                        <TooltipTrigger>
                            <div className="flex items-center">
                                <ChevronDoubleDownIcon className="w-5 h-5 text-red-600" />
                                <div className="font-semibold text-md">{siteCost.debuff}</div>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent className="max-w-full w-96 px-4" style={{ zIndex: 100 }}>
                            <div className="rounded-md bg-gray-900 border border-color-border shadow p-4 text-sm text-white">
                                <div>Times the site has failed to level up in a row.</div>
                                <div className="text-contrast-tertiary text-xs">Site costs weren't paid for the past # months.</div>
                            </div>
                        </TooltipContent>
                    </Tooltip>
                }
            </div>

            <Tooltip>
                <TooltipTrigger>
                    <div className="flex items-center gap-1">
                        <div className="font-semibold w-5 mr-[1px] mb-[1px]">xp</div>
                        <div className="flex-1 relative">
                            <SimpleProgress progress={siteCost.progress} classNames={{ container: "flex-1" }} />
                            {
                                isProgressHover &&
                                <span className="absolute leading-3 top-0 text-center w-full font-semibold text-sm -mt-[2px]" style={{ filter: "drop-shadow(0 0 2px black) drop-shadow(0 0 4px black) drop-shadow(0 0 6px black)" }}>{siteCost.progress}%</span>
                            }
                        </div>
                    </div>
                </TooltipTrigger>
                <TooltipContent className="max-w-full w-96 px-4" style={{ zIndex: 100 }}>
                    <div className="rounded-md bg-gray-900 border border-color-border shadow p-4 text-sm text-white">
                        <div>XP towards the next site level.</div>
                        <div className="text-contrast-tertiary text-xs">Progress towards paying off site costs for the current month.</div>
                    </div>
                </TooltipContent>
            </Tooltip>

            {
                siteCost.progress < 100 &&
                <Tooltip>
                    <TooltipTrigger>
                        <div className="flex items-center gap-1">
                            <ClockIcon className="w-5 h-5 mr-[1px]" />
                            <SimpleProgress
                                progress={siteCost.time}
                                classNames={{
                                    container: "flex-1",
                                    bar: classNames(
                                        siteCost.time < 60 && "!bg-green-600",
                                        (siteCost.time >= 60 && siteCost.time < 80) && "!bg-yellow-600",
                                        siteCost.time >= 80 && "!bg-red-600",
                                    )
                                }} />

                        </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-full w-96 px-4" style={{ zIndex: 100 }}>
                        <div className="rounded-md bg-gray-900 border border-color-border shadow p-4 text-sm text-white">
                            <div>Time remaining to reach the next level.</div>
                            <div className="text-contrast-tertiary text-xs mb-2">Time remaining in current month to pay site cost.</div>

                            <div className="text-contrast-tertiary text-xs mb-2">Note: Bar increases, not decreases.</div>
                        </div>
                    </TooltipContent>
                </Tooltip>
            }

            {
                siteCost.progress >= 100 &&
                <Tooltip>
                    <TooltipTrigger>
                        <div className="flex w-full justify-center mt-1">
                            {
                                isProgressHover
                                    ? <img
                                        src={`${FIREBASE_STORAGE_URL}/images/victory.gif`}
                                        alt={`victory image`}
                                        loading="lazy" />
                                    : <img
                                        src={`${FIREBASE_STORAGE_URL}/images/victory.png`}
                                        alt={`victory image`}
                                        loading="lazy" />
                            }
                        </div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-full w-96 px-4" style={{ zIndex: 100 }}>
                        <div className="rounded-md bg-gray-900 border border-color-border shadow p-4 text-sm text-white">
                            <div>Max XP reached!</div>
                            <div className="text-contrast-tertiary text-xs mb-2">Server costs were paid in full this month via donations.</div>
                        </div>
                    </TooltipContent>
                </Tooltip>
            }
        </a>
    )
}